import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  Textarea,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  useToast,
  Badge,
  Spinner,
  Icon,
  Divider,
  SimpleGrid,
  Grid,
  GridItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  CloseButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Center,
  Image,
  IconButton,
  Progress
} from '@chakra-ui/react';
import { FiRefreshCw, FiUploadCloud, FiDownloadCloud, FiSettings, FiCheck, FiX } from 'react-icons/fi';
import { FaPlay, FaPause, FaStop, FaTrash, FaDownload, FaUndo, FaRedo, FaExclamationTriangle, FaSync, FaMusic, FaSave } from 'react-icons/fa';
import { presentationAuth, signOutCompletely } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import PresenterPhotoManager from './PresenterPhotoManager';
import { useNarrationSync } from '../scripts/unifiedNarrationSync';
import { SlideNarration, DashboardNarration } from '../types/narration';
import AdminLayout from './layout/AdminLayout';

const AdminPage: React.FC = () => {
  // Methodist brand colors
  const methodistBlue = '#0066CC';
  const methodistNavy = '#002D72';
  
  // Disable toasts to prevent infinite loops during development
  const disableToasts = true;
  
  // State for ElevenLabs API key and usage
  const [elevenLabsApiKey, setElevenLabsApiKey] = useState<string>(
    localStorage.getItem('elevenLabsApiKey') || ''
  );
  const [characterUsage, setCharacterUsage] = useState<number>(0);
  const [maxCharacters, setMaxCharacters] = useState<number>(0);
  const [showApiKey, setShowApiKey] = useState(false);

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Narration state
  const [narrations, setNarrations] = useState<(SlideNarration | DashboardNarration)[]>([]);
  const [loadingNarrations, setLoadingNarrations] = useState<boolean>(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // UI state
  const [activeTab, setActiveTab] = useState<number>(0);
  
  // Hooks
  const navigate = useNavigate();
  const toast = useToast();
  const isFirstMount = useRef(true);

  // Use the narration sync hook
  const {
    isLoading: isSyncLoading,
    error: syncError,
    syncStatus,
    fetchNarrations: fetchNarrationsFromFirebase,
    syncAllToFirebase,
    syncToCodebase,
    testFirebaseConnection,
  } = useNarrationSync();

  // Check if user is already authenticated
  useEffect(() => {
    // For presentation mode, we'll bypass the authentication check
    const isPresentationMode = false; // Set to false to use proper Firebase authentication
    
    if (isPresentationMode) {
      console.log('[AUTH] Presentation mode detected, bypassing auth check');
      setIsAuthenticated(true);
      setIsAdmin(true);
      return;
    }
    
    console.log('[ADMIN AUTH] Setting up auth state listener');
    setIsAuthenticated(null); // Set to null while checking
    
    const unsubscribe = presentationAuth.onAuthStateChanged(async (user) => {
      console.log('[ADMIN AUTH] Auth state changed:', user?.email);
      
      if (user) {
        // For now, consider all authenticated users as admins to prevent login issues
        // This can be replaced with proper admin verification later
        setIsAuthenticated(true);
        setIsAdmin(true);
        
        // Log the successful authentication
        console.log('[ADMIN AUTH] User authenticated successfully');
        
        // Check if we have the admin email in localStorage for additional verification
        const storedEmail = localStorage.getItem('admin_email');
        const storedAuth = localStorage.getItem('admin_authenticated');
        
        if (!storedEmail || !storedAuth) {
          // Store authentication state for additional persistence
          localStorage.setItem('admin_email', user.email || '');
          localStorage.setItem('admin_authenticated', 'true');
        }
        
        if (!disableToasts) {
          toast({
            title: 'Welcome back',
            description: `Logged in as ${user.email}`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
      } else {
        // Check if we have stored authentication that might have been missed
        const storedEmail = localStorage.getItem('admin_email');
        const storedAuth = localStorage.getItem('admin_authenticated');
        
        if (storedEmail && storedAuth === 'true') {
          console.log('[ADMIN AUTH] Found stored authentication, attempting to stay logged in');
          // We'll consider the user still authenticated based on localStorage
          // This helps prevent flickering during auth state transitions
          setIsAuthenticated(true);
          setIsAdmin(true);
        } else {
          console.log('[ADMIN AUTH] No authentication found');
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      }
    });
    return () => unsubscribe();
  }, [toast, disableToasts]);

  // Redirect to login if not authenticated
  useEffect(() => {
    // Only redirect if explicitly not authenticated (false)
    // This prevents redirection during the initial loading state (null)
    if (isAuthenticated === false) {
      console.log('[ADMIN AUTH] Not authenticated, redirecting to login');
      
      // Clear any stored authentication data
      localStorage.removeItem('admin_email');
      localStorage.removeItem('admin_authenticated');
      
      // Add a small delay to prevent rapid redirects
      setTimeout(() => {
        navigate('/admin/login');
      }, 100);
    }
  }, [isAuthenticated, navigate]);

  // Handle API key change
  const handleApiKeyChange = (apiKey: string) => {
    setElevenLabsApiKey(apiKey);
    localStorage.setItem('elevenLabsApiKey', apiKey);
  };

  // Handle character usage change
  const handleCharacterUsageChange = (usage: number, limit: number) => {
    setCharacterUsage(usage);
    setMaxCharacters(limit);
  };

  // Fetch narrations from Firebase
  const fetchNarrations = useCallback(async () => {
    setLoadingNarrations(true);
    setSyncMessage('Fetching narrations from Firebase...');

    try {
      const fetchedNarrations = await fetchNarrationsFromFirebase();
      setNarrations(fetchedNarrations);
      setSyncMessage(null);

      // Only show toast if not called during initial mount
      if (!isFirstMount.current && !disableToasts) {
        toast({
          title: 'Narrations loaded',
          description: `Successfully loaded ${fetchedNarrations.length} narrations from Firebase.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching narrations:', error);
      setSyncMessage(`Error fetching narrations: ${error instanceof Error ? error.message : 'Unknown error'}`);

      // Only show toast if not called during initial mount
      if (!isFirstMount.current && !disableToasts) {
        toast({
          title: 'Error loading narrations',
          description: error instanceof Error ? error.message : 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setLoadingNarrations(false);
    }
  }, [fetchNarrationsFromFirebase, disableToasts]);

  // Handle sync all to Firebase
  const handleSyncAllToFirebase = async () => {
    setSyncMessage('Syncing all narrations to Firebase...');

    try {
      await syncAllToFirebase();
      if (!disableToasts) {
        toast({
          title: 'Firebase sync complete',
          description: 'All narrations successfully synced to Firebase.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      setSyncMessage('Firebase sync complete');

      // Refresh narrations
      fetchNarrations();
      return true;
    } catch (error) {
      console.error('Error syncing to Firebase:', error);
      if (!disableToasts) {
        toast({
          title: 'Firebase sync failed',
          description: error instanceof Error ? error.message : 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      setSyncMessage(`Error syncing to Firebase: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    }
  };

  // Handle sync Firebase to codebase
  const handleSyncToCodebase = async () => {
    setSyncMessage('Syncing Firebase to codebase...');

    try {
      await syncToCodebase();
      if (!disableToasts) {
        toast({
          title: 'Codebase sync complete',
          description: 'Narrations successfully synced from Firebase to codebase.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      setSyncMessage('Codebase sync complete');
      return true;
    } catch (error) {
      console.error('Error syncing Firebase to codebase:', error);
      if (!disableToasts) {
        toast({
          title: 'Sync to codebase failed',
          description: error instanceof Error ? error.message : 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      setSyncMessage('Error syncing Firebase to codebase');
      return false;
    }
  };

  // Initialize Firebase connection
  const initializeFirebaseConnection = useCallback(async () => {
    try {
      const hasNarrations = await testFirebaseConnection();
      
      if (!hasNarrations) {
        if (!disableToasts) {
          toast({
            title: 'Firebase Initialization',
            description: 'No narrations found in Firebase. You may need to sync from codebase first.',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        if (!disableToasts) {
          toast({
            title: 'Firebase Connection',
            description: 'Successfully connected to Firebase narration database.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
      }
      
      return hasNarrations;
    } catch (error) {
      console.error('Error testing Firebase connection:', error);
      if (!disableToasts) {
        toast({
          title: 'Firebase Connection Error',
          description: error instanceof Error ? error.message : 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      
      return false;
    }
  }, [testFirebaseConnection, disableToasts]);

  // Handle save narration
  const handleSaveNarration = (narration: SlideNarration | DashboardNarration) => {
    setNarrations(prevNarrations => {
      return prevNarrations.map(n => {
        if ((isSlideNarration(n) && isSlideNarration(narration) && n.slideId === narration.slideId) ||
            (isDashboardNarration(n) && isDashboardNarration(narration) && n.dashboardId === narration.dashboardId)) {
          return narration;
        }
        return n;
      });
    });
  };

  // Helper functions for type checking narrations
  const isSlideNarration = (narration: SlideNarration | DashboardNarration): narration is SlideNarration => {
    return (narration as SlideNarration).slideId !== undefined;
  };

  const isDashboardNarration = (narration: SlideNarration | DashboardNarration): narration is DashboardNarration => {
    return (narration as DashboardNarration).dashboardId !== undefined;
  };

  // Handle batch process
  const handleBatchProcess = (updatedNarrations: (SlideNarration | DashboardNarration)[]) => {
    setNarrations(updatedNarrations);
  };

  // Handle export
  const handleExport = () => {
    if (!disableToasts) {
      toast({
        title: 'Export feature',
        description: 'Export functionality will be implemented soon.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle import
  const handleImport = (file: File) => {
    if (!disableToasts) {
      toast({
        title: 'Import feature',
        description: 'Import functionality will be implemented soon.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      console.log('[ADMIN AUTH] Logging out user');
      setIsLoading(true);
      
      // Use the enhanced signOutCompletely function
      const success = await signOutCompletely();
      
      if (success) {
        // Update state
        setIsAuthenticated(false);
        setIsAdmin(false);
        
        // Show success toast
        if (!disableToasts) {
          toast({
            title: 'Logged out successfully',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
        
        // Redirect to login page
        navigate('/admin/login');
      } else {
        throw new Error('Logout failed');
      }
    } catch (error) {
      console.error('[ADMIN AUTH] Error during logout:', error);
      
      if (!disableToasts) {
        toast({
          title: 'Logout error',
          description: 'There was an error logging out. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, toast, disableToasts]);

  // Load narrations on mount
  useEffect(() => {
    fetchNarrations();
    isFirstMount.current = false;
  }, [fetchNarrations]);

  return (
    <AdminLayout
      title="Houston Methodist Administration Portal"
      isLoading={loadingNarrations || isSyncLoading}
      onRefresh={fetchNarrations}
      onSyncToFirebase={handleSyncAllToFirebase}
      onSyncToCodebase={handleSyncToCodebase}
      onTestConnection={initializeFirebaseConnection}
      onLogout={handleLogout}
    >
      {/* Sync Status Messages */}
      {syncMessage && (
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon />
          {syncMessage}
        </Alert>
      )}
      
      {/* Main Content */}
      <Tabs variant="enclosed" colorScheme="blue" index={activeTab} onChange={setActiveTab}>
        <TabList mb={4}>
          <Tab _selected={{ color: 'white', bg: methodistBlue }} fontWeight="medium">Media</Tab>
        </TabList>
        <TabPanels>
          {/* Media Tab */}
          <TabPanel p={0}>
            <Box p={4} borderWidth="1px" borderRadius="md" bg="white">
              <Heading size="md" mb={4} color={methodistNavy}>Presenter Photo Management</Heading>
              <PresenterPhotoManager />
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
      
      {/* TTS Studio Navigation Button */}
      <Box mt={4} textAlign="center">
        <Button
          leftIcon={<FaMusic />}
          colorScheme="blue"
          onClick={() => navigate('/admin/tts-studio')}
          size="md"
        >
          Open TTS Studio
        </Button>
      </Box>
    </AdminLayout>
  );
};

export default AdminPage;
